import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import {
  getScrollContainer,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const VIEW_TIMELINE_SUPPORT = '(animation-timeline: view()) and (animation-range: cover)';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [media] - The clipped background media layer.
 *
 * @extends {Component<Refs>}
 */
export class SkreyParallax extends Component {
  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {number | null} */
  #syncRafId = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #inView = false;

  /** @type {boolean} */
  #usesCssTimeline = false;

  /** @type {number} */
  #travel = 0;

  /** @type {number} */
  #zoom = 1;

  /** @type {string} */
  #appliedTravel = '';

  connectedCallback() {
    super.connectedCallback();

    this.#usesCssTimeline = this.#supportsViewTimeline();
    this.#cacheMotionValues();

    if (prefersReducedMotion()) return;

    this.#scheduleSyncTravel();
    this.#observeResize();
    this.#bindImageLoad();

    if (this.#usesCssTimeline) return;

    this.#observeVisibility();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cacheMotionValues();
    if (!prefersReducedMotion()) this.#scheduleSyncTravel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * @returns {boolean}
   */
  #supportsViewTimeline() {
    return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports(VIEW_TIMELINE_SUPPORT);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getImage() {
    const media = this.refs.media;
    if (!(media instanceof HTMLElement)) return null;

    const image = media.querySelector('.skrey-parallax__image');
    return image instanceof HTMLElement ? image : null;
  }

  #cacheMotionValues() {
    const strength = Number.parseFloat(this.dataset.parallaxStrength ?? '');
    const zoom = Number.parseFloat(this.dataset.parallaxZoom ?? '');
    const originY = Number.parseFloat(this.dataset.parallaxOriginY ?? '');

    this.#zoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
    this.#strength = Number.isFinite(strength) ? Math.min(100, Math.max(0, strength)) : 100;
    this.#originY = Number.isFinite(originY) ? originY / 100 : 0.5;
  }

  /** @type {number} */
  #strength = 100;

  /** @type {number} */
  #originY = 0.5;

  #observeResize() {
    this.#resizeObserver?.disconnect();

    const media = this.refs.media;
    if (!(media instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(this.#scheduleSyncTravel);
    this.#resizeObserver.observe(media);
  }

  #bindImageLoad() {
    const image = this.#getImage();
    if (!(image instanceof HTMLImageElement) || image.complete) return;

    image.addEventListener('load', this.#scheduleSyncTravel, { once: true });
  }

  #scheduleSyncTravel = () => {
    if (this.#syncRafId !== null) return;

    this.#syncRafId = requestAnimationFrame(() => {
      this.#syncRafId = null;
      this.#syncTravel();
    });
  };

  /**
   * Applied travel is strength (0–100) times the overflow that still covers the clip.
   * Writes the CSS variable only on the media layer so text blocks do not recalc.
   */
  #syncTravel() {
    const media = this.refs.media;
    if (!(media instanceof HTMLElement)) return;

    const applied = `${((this.#measureMaxTravelPercent() * this.#strength) / 100).toFixed(3)}%`;
    if (applied === this.#appliedTravel) return;

    this.#appliedTravel = applied;
    this.#travel = Number.parseFloat(applied) || 0;
    media.style.setProperty('--skrey-parallax-travel', applied);
  }

  /**
   * @returns {number}
   */
  #measureMaxTravelPercent() {
    const media = this.refs.media;
    const image = this.#getImage();
    if (!(media instanceof HTMLElement) || !image) return 0;

    const clipHeight = media.clientHeight;
    const layoutHeight = image.offsetHeight;
    if (clipHeight <= 0 || layoutHeight <= 0) return 0;

    const layoutTop = image.offsetTop;
    const originYpx = layoutTop + this.#originY * layoutHeight;
    const scaledHeight = layoutHeight * this.#zoom;
    const scaledTop = originYpx - this.#originY * scaledHeight;
    const maxPx = Math.max(0, Math.min(0 - scaledTop, scaledTop + scaledHeight - clipHeight) - 1);

    return (maxPx / layoutHeight) * 100;
  }

  #observeVisibility() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        this.#inView = Boolean(entry?.isIntersecting);

        if (this.#inView) {
          this.#bindScroll();
          this.#update();
        } else {
          this.#unbindScroll();
        }
      },
      { root: getIntersectionRoot(), threshold: 0 }
    );

    this.#intersectionObserver.observe(this);
  }

  #bindScroll() {
    if (this.#scrollTarget) return;

    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #unbindScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;
  }

  #handleBreakpointChange = () => {
    this.#unbindScroll();
    this.#scheduleSyncTravel();
    this.#observeVisibility();
    if (this.#inView) {
      this.#bindScroll();
      this.#update();
    }
  };

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#update();
    });
  };

  #update = () => {
    const image = this.#getImage();
    if (!image) return;

    const rect = this.getBoundingClientRect();
    const viewportHeight = getScrollContainer().clientHeight;
    const distance = viewportHeight + rect.height;
    const progress = distance === 0 ? 0 : (viewportHeight - rect.top) / distance;
    const clamped = Math.min(1, Math.max(0, progress));
    const offset = (clamped * 2 - 1) * this.#travel;

    image.style.transform = `translate3d(0, ${offset}%, 0) scale(${this.#zoom})`;
  };

  #teardown() {
    this.#unbindScroll();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    const image = this.#getImage();
    if (image instanceof HTMLImageElement) {
      image.removeEventListener('load', this.#scheduleSyncTravel);
    }

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    if (this.#syncRafId !== null) {
      cancelAnimationFrame(this.#syncRafId);
      this.#syncRafId = null;
    }
  }
}

if (!customElements.get('skrey-parallax')) {
  customElements.define('skrey-parallax', SkreyParallax);
}
